<script setup lang="ts">
import { ref, computed } from 'vue';
import type { ProductWithDetails } from '../../types';
import { useCategoryStore, useLaboratoryStore, useConfigStore } from '../../stores';
import { useProductStore } from '../../stores/productStore';
import { useNotificationStore } from '../../stores/notificationStore';
import ProductLaboratoryForm from './ProductLaboratoryForm.vue';
import SimilarProductsModal from './SimilarProductsModal.vue';

const props = defineProps<{
  initialData?: ProductWithDetails | null;
  saving?: boolean;
}>();

const emit = defineEmits<{
  submit: [ProductWithDetails];
  cancel: [];
}>();

const categoryStore = useCategoryStore();
const laboratoryStore = useLaboratoryStore();
const configStore = useConfigStore();
const productStore = useProductStore();
const notificationStore = useNotificationStore();

// Claves para localStorage
const LAST_CATEGORY_KEY = 'lastSelectedCategory';
const LAST_LABORATORY_KEY = 'lastSelectedLaboratory';

// Cargar última categoría y laboratorio seleccionados
const getLastCategory = () => {
  const lastCategory = localStorage.getItem(LAST_CATEGORY_KEY);
  return lastCategory && categoryStore.categories[lastCategory] 
    ? lastCategory 
    : Object.keys(categoryStore.categories)[0];
};

const getLastLaboratory = () => {
  const lastLabId = localStorage.getItem(LAST_LABORATORY_KEY);
  return laboratoryStore.laboratories.find(lab => lab.id === lastLabId) || 
         laboratoryStore.laboratories[0];
};

// Crear un producto nuevo o clonar el existente
const formData = ref<ProductWithDetails>(
  props.initialData ? {
    ...JSON.parse(JSON.stringify(props.initialData)),
    destacado: props.initialData.destacado || {
      activo: false,
      mensaje: ''
    },
    similarProducts: props.initialData.similarProducts || []
  } : {
    Nombre: '',
    Rango: '',
    Categoria: getLastCategory(),
    Factor: 2.0,
    destacado: {
      activo: false,
      mensaje: ''
    },
    similarProducts: [],
    laboratories: [{
      laboratory: getLastLaboratory(),
      details: {
        productId: '',
        laboratoryId: getLastLaboratory()?.id || '',
        PrecioDeCosto: 0,
        TiempoDeDemora: '24hs',
        Disponible: true,
        UltimaActualizacion: new Date(),
        graduations: []
      }
    }]
  }
);

// Estado para el modal de productos similares
const showSimilarProductsModal = ref(false);
const validationError = ref('');

// Configuración de la categoría actual
const categoryConfig = computed(() => 
  configStore.getCategoryConfig(formData.value.Categoria)
);

// Guardar selecciones cuando cambian
const handleCategoryChange = (categoryId: string) => {
  formData.value.Categoria = categoryId;
  localStorage.setItem(LAST_CATEGORY_KEY, categoryId);
};

const handleLaboratoryChange = (labId: string) => {
  const laboratory = laboratoryStore.laboratories.find(lab => lab.id === labId);
  if (laboratory) {
    formData.value.laboratories[0].laboratory = laboratory;
    formData.value.laboratories[0].details.laboratoryId = labId;
    localStorage.setItem(LAST_LABORATORY_KEY, labId);
  }
};

const handleSimilarProductsUpdate = (selectedIds: string[]) => {
  formData.value.similarProducts = selectedIds;
  showSimilarProductsModal.value = false;
};

// Verificar si ya existe un producto con el mismo nombre y categoría
const checkDuplicateProduct = (): boolean => {
  // Si estamos editando, excluimos el producto actual de la verificación
  const existingProducts = props.initialData 
    ? productStore.products.filter(p => p.id !== props.initialData.id)
    : productStore.products;
  
  const normalizedName = formData.value.Nombre.trim().toLowerCase();
  const category = formData.value.Categoria;
  
  const duplicate = existingProducts.find(p => 
    p.Nombre.trim().toLowerCase() === normalizedName && 
    p.Categoria === category
  );
  
  if (duplicate) {
    validationError.value = `Ya existe un producto con el nombre "${formData.value.Nombre}" en la categoría "${categoryStore.categories[category]}"`;
    return true;
  }
  
  return false;
};

const handleSubmit = () => {
  // Limpiar error previo
  validationError.value = '';
  
  // Validar que no exista un producto con el mismo nombre y categoría
  if (checkDuplicateProduct()) {
    notificationStore.error(validationError.value);
    return;
  }
  
  // Guardar última categoría y laboratorio
  localStorage.setItem(LAST_CATEGORY_KEY, formData.value.Categoria);
  localStorage.setItem(LAST_LABORATORY_KEY, formData.value.laboratories[0].laboratory.id!);
  
  emit('submit', formData.value);
};
</script>

<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-2">Nombre</label>
        <input v-model="formData.Nombre" class="input-field" required />
        <p v-if="validationError" class="mt-1 text-sm text-red-600">{{ validationError }}</p>
      </div>

      <div>
        <label class="block text-sm font-medium text-slate-700 mb-2">Categoría</label>
        <select 
          :value="formData.Categoria" 
          class="input-field" 
          required
          @change="handleCategoryChange($event.target.value)"
        >
          <option v-for="[id, label] in Object.entries(categoryStore.categories)" :key="id" :value="id">
            {{ label }}
          </option>
        </select>
      </div>

      <div v-if="categoryConfig.hasNotes">
        <label class="block text-sm font-medium text-slate-700 mb-2">Observaciones</label>
        <input 
          v-model="formData.Rango"
          class="input-field"
          placeholder="Ingrese las observaciones del producto"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-slate-700 mb-2">Factor</label>
        <input 
          v-model.number="formData.Factor"
          type="number"
          min="0"
          step="0.01"
          class="input-field"
          required
        />
      </div>
    </div>

    <!-- Destacado -->
    <div class="bg-slate-50 p-4 rounded-lg">
      <div class="flex items-center gap-2 mb-4">
        <input
          type="checkbox"
          v-model="formData.destacado.activo"
          class="rounded text-indigo-600 focus:ring-indigo-500"
        />
        <label class="text-sm font-medium text-slate-700">Destacar producto</label>
      </div>
      
      <div v-if="formData.destacado.activo">
        <label class="block text-sm font-medium text-slate-700 mb-2">
          Mensaje de destacado
        </label>
        <input
          v-model="formData.destacado.mensaje"
          class="input-field"
          placeholder="Ej: ¡Nuevo producto! / ¡Oferta especial!"
          required
        />
      </div>
    </div>

    <!-- Similar Products Button -->
    <div v-if="categoryConfig.allowSimilarProducts" class="bg-slate-50 p-4 rounded-lg">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="text-sm font-medium text-slate-700">Productos Similares</h4>
          <p class="text-xs text-slate-500 mt-1">
            Seleccione productos similares para comparación de precios
          </p>
        </div>
        <button
          type="button"
          @click="showSimilarProductsModal = true"
          class="btn-primary bg-white border border-indigo-500 text-indigo-500 hover:bg-indigo-50"
        >
          {{ formData.similarProducts?.length 
            ? `${formData.similarProducts.length} productos seleccionados` 
            : 'Seleccionar Productos' 
          }}
        </button>
      </div>
    </div>

    <ProductLaboratoryForm
      v-model="formData.laboratories"
      :laboratories="formData.laboratories"
      :show-graduations="categoryConfig.hasGraduations"
      :product-name="formData.Nombre"
      @laboratory-change="handleLaboratoryChange"
    />

    <div class="flex justify-end space-x-4">
      <button 
        type="button"
        @click="emit('cancel')"
        class="px-4 py-2 text-slate-600 hover:text-slate-800"
        :disabled="saving"
      >
        Cancelar
      </button>
      <button 
        type="submit"
        class="btn-primary relative"
        :disabled="saving"
      >
        <span :class="{ 'opacity-0': saving }">
          {{ saving ? 'Guardando...' : (props.initialData ? 'Guardar Cambios' : 'Crear Producto') }}
        </span>
        <span 
          v-if="saving" 
          class="absolute inset-0 flex items-center justify-center"
        >
          Guardando...
        </span>
      </button>
    </div>

    <!-- Similar Products Modal -->
    <SimilarProductsModal
      v-if="showSimilarProductsModal"
      :show="showSimilarProductsModal"
      :current-product="formData"
      :available-products="productStore.products"
      :initial-selection="formData.similarProducts"
      @close="showSimilarProductsModal = false"
      @save="handleSimilarProductsUpdate"
    />
  </form>
</template>

<style scoped>
.input-field {
  @apply w-full p-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 
         focus:border-indigo-500 outline-none transition-all duration-200;
}
</style>